import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from "react";
import {
  getStatementByAccount,
  getStatementToExport,
  getStatementPDF,
  sendStatementByEmail,
  getAgedBalancesByAccount,
  getReceiptByNumber,
  sendInvoiceByEmail,
  getCreditNoteByNumber,
  updateAccountEdi,
  updateAccountShowOnStatement,
} from "../../afinanceapi";
import { getUserPracticeById, getPracticeById } from "../../api";
import {
  Form,
  Button,
  Row,
  Col,
  Table,
  Container,
  Modal,
} from "react-bootstrap";
import BulkExportModal from "./BulkExportModal";
import CreateReceiptModal from "../Accounts/Receipt";
import CreditNote from "./CreditNote";
import WriteOffModal from "./WriteOffModal";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import { getBookingsReturnPath } from "../../utils/bookingsReturn";
import "./AccountStatement.css";
import EmailStatementModal from "./EmailStatement";
import { ExportPDFReceipt } from "./ExportReceiptPDF";
import EmailDocumentModal from "./EmailDocumentModal";

const AccountStatements = forwardRef(function AccountStatements(
  { account, breadcrumbContext, onStateChange },
  ref,
) {
  const [accountId, setAccountId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [practiceIds, setPracticeIds] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [practice, setPractice] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [showWriteOffModal, setShowWriteOffModal] = useState(false);
  const [receiptAccount, setReceiptAccount] = useState(null);
  const [creditNoteAccount, setCreditNoteAccount] = useState(null);
  const [statementBlob, setStatementBlob] = useState(null);
  const [agedBalances, setAgedBalances] = useState([]);
  const [statementData, setStatementData] = useState(null);
  const [selectedReceiptEntry, setSelectedReceiptEntry] = useState(null);
  const [showReceiptActionModal, setShowReceiptActionModal] = useState(false);
  const [showStatementEmailModal, setShowStatementEmailModal] = useState(false);
  const [showReceiptEmailModal, setShowReceiptEmailModal] = useState(false);
  const [showBulkExport, setShowBulkExport] = useState(false);
  const [unallocatedBalance, setUnallocatedBalance] = useState(0);
  const [unallocatedReceipts, setUnallocatedReceipts] = useState([]);
  const [showAllUnallocated, setShowAllUnallocated] = useState(false);
  const [showOnStatement, setShowOnStatement] = useState(true);
  const [userRole, setUserRole] = useState(false);
  const [docEmailBlob, setDocEmailBlob] = useState(null); // PDF for receipt or credit-note
  const [docEmailType, setDocEmailType] = useState("receipt"); // "receipt" | "creditNote"
  const [docEmailData, setDocEmailData] = useState(null); // emailData to send to backend
  const [showEdiModal, setShowEdiModal] = useState(false);
  const [ediDisabled, setEdiDisabled] = useState(false);
  const [ediEndDate, setEdiEndDate] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  // Initialize EDI state from account prop
  useEffect(() => {
    if (account) {
      setEdiDisabled(!!account.ediDisabled);
      setEdiEndDate(account.ediEndDate || "");
      setShowOnStatement(account.showOnStatement !== false); // default true
    }
  }, [account]);

  // Initialize dates once per accountId — priority: URL params > sessionStorage
  // > default (last 1 month). sessionStorage is keyed per-account so each
  // account remembers its own filter when the user navigates away and back
  // (e.g. via the breadcrumb, which doesn't carry the URL params).
  const datesInitialized = useRef(null);
  const stashKey = account?._id ? `accountStatementDates:${account._id}` : null;

  useEffect(() => {
    // Re-init when navigating between different accounts.
    if (!account?._id) return;
    if (datesInitialized.current === account._id) return;
    datesInitialized.current = account._id;

    const params = new URLSearchParams(window.location.search);
    const urlStart = params.get('start');
    const urlEnd = params.get('end');

    let s, e;
    if (urlStart && urlEnd) {
      s = urlStart;
      e = urlEnd;
    } else {
      try {
        const stashed = sessionStorage.getItem(`accountStatementDates:${account._id}`);
        if (stashed) {
          const parsed = JSON.parse(stashed);
          if (parsed?.start && parsed?.end) {
            s = parsed.start;
            e = parsed.end;
          }
        }
      } catch { /* ignore corrupted stash */ }

      if (!s || !e) {
        const today = new Date();
        const oneMonthBefore = new Date();
        oneMonthBefore.setMonth(today.getMonth() - 1);
        s = oneMonthBefore.toISOString().split("T")[0];
        e = today.toISOString().split("T")[0];
      }
    }
    setStartDate(s);
    setEndDate(e);
  }, [account?._id]);

  // Write date params to URL AND mirror to sessionStorage so the filter
  // survives navigation that drops the URL search string.
  useEffect(() => {
    if (!startDate || !endDate) return;
    const params = new URLSearchParams(location.search);
    params.set('start', startDate);
    params.set('end', endDate);
    navigate({ search: params.toString() }, { replace: true, state: location.state });
    if (stashKey) {
      try {
        sessionStorage.setItem(stashKey, JSON.stringify({ start: startDate, end: endDate }));
      } catch { /* storage full / disabled — not fatal */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, stashKey]);

  useEffect(() => {
    async function fetchAccountData() {
      if (account && account._id) {
        setAccountId(account._id);
        const token = sessionStorage.getItem("user");
        const user = jwtDecode(token);
        const practiceData = await getUserPracticeById(user._id);
        setPractice(practiceData);
        setPracticeIds(user.practice || []);
        const rolesFromRole = Array.isArray(user?.role)
          ? user.role
          : user?.role
          ? [user.role]
          : [];
        const rolesFromPermissions = Array.isArray(user?.permissions)
          ? user.permissions
          : [];

        const allRoles = [
          ...new Set([...rolesFromRole, ...rolesFromPermissions]),
        ];
        const allowed = new Set(["Finance", "SuperAdmin"]);
        const hasAllowedRole = allRoles.some((r) => allowed.has(r));

        setUserRole(!!hasAllowedRole);

        // Dates are initialized once on mount (see datesInitialized ref above)
      }
    }
    fetchAccountData();
  }, [account]);

  function formatDate(isoString) {
    return isoString ? isoString.split("T")[0] : "";
  }

  const fetchData = useCallback(async function fetchData() {
    if (!accountId || !startDate || !endDate || practiceIds.length === 0)
      return;

    try {
      const data = await getStatementByAccount(
        accountId,
        startDate,
        endDate,
        practiceIds
      );
      const {
        invoices = [],
        stragglerReceipts = [],
        openingBalance = 0,
        closingBalance = 0,
        unallocatedReceipts: unallocRcpts = [],
      } = data;
      setUnallocatedReceipts(unallocRcpts);

      const transactions = [];
      // Opening Balance
      transactions.push({
        date: formatDate(startDate),
        description: "Opening Balance",
        invoiceAmount: 0,
        receiptFromPatient: 0,
        receiptFromMedicalAid: 0,
        balance: openingBalance,
      });

      // Merge invoices + straggler receipts (in-period receipts against
      // out-of-range invoices) into a single chronological timeline so the
      // running balance column reads consistently.
      const timeline = [
        ...invoices.map((inv) => ({
          kind: "invoice",
          date: String(inv.invoiceDate || "").slice(0, 10),
          data: inv,
        })),
        ...stragglerReceipts.map((s) => ({
          kind: "straggler",
          date: String(s.receiptDate || "").slice(0, 10),
          data: s,
        })),
      ].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

      timeline.forEach((entry) => {
        if (entry.kind === "invoice") {
          const inv = entry.data;
          const invoiceAmount = parseFloat(inv.invoiceTotalAmount || 0);

          transactions.push({
            invoiceId: inv._id,
            invoice: inv,
            date: formatDate(inv.invoiceDate),
            description: `Invoice-${inv.invoiceNumber}`,
            status: inv.status,
            invoiceAmount,
            receiptFromPatient: inv.patientTotal,
            receiptFromMedicalAid: inv.medicalAidTotal,
            balance: inv.runningBalance,
          });

          inv.receipts.forEach((r) => {
            const num =
              r.receiptNumber || r.creditNoteNumber || r.refundNumber || r.writeOffNumber;
            const dt =
              r.receiptDate || r.creditNoteDate || r.refundDate || r.writeOffDate;
            const isWriteOff = r.paymentSource === "writeOff";

            let fromPatient = 0;
            let fromMedAid = 0;
            if (isWriteOff) {
              fromPatient = r.writeOffPatientAmount || 0;
              fromMedAid = r.writeOffMedAidAmount || 0;
            } else if (["Patient", "patient"].includes(r.paymentSource)) {
              fromPatient = r.receiptAmount || r.creditNoteAmount || 0;
            } else if (["Medical Aid", "medicalAid"].includes(r.paymentSource)) {
              fromMedAid = r.receiptAmount || r.creditNoteAmount || 0;
            }

            transactions.push({
              receiptNumber: num,
              receiptDate: dt,
              paymentAmount:
                r.receiptAmount || r.creditNoteAmount || r.refundAmount || r.writeOffAmount || 0,
              receiptReference:
                r.receiptReference ||
                r.creditNoteReference ||
                r.refundReference ||
                "",
              practiceNumber: inv.practiceNumber,
              date: "",
              description: `${num} (${dt})`,
              status: "",
              paymentSource: r.paymentSource,
              receiptFromPatient: fromPatient,
              receiptFromMedicalAid: fromMedAid,
            });
          });
        } else {
          // Straggler — standalone row labeled against parent invoice, with
          // running balance (under-invoice receipts don't show balance because
          // the parent invoice row already does).
          const s = entry.data;
          const num = s.receiptNumber;
          const dt = s.receiptDate;
          const parentInv = s.parentInvoice?.invoiceNumber || "";
          const isWriteOff = s.paymentSource === "writeOff";

          let fromPatient = 0;
          let fromMedAid = 0;
          if (isWriteOff) {
            fromPatient = s.writeOffPatientAmount || 0;
            fromMedAid = s.writeOffMedAidAmount || 0;
          } else if (["Patient", "patient"].includes(s.paymentSource)) {
            fromPatient = s.receiptAmount || 0;
          } else if (["Medical Aid", "medicalAid"].includes(s.paymentSource)) {
            fromMedAid = s.receiptAmount || 0;
          }

          transactions.push({
            receiptNumber: num,
            receiptDate: dt,
            paymentAmount: s.receiptAmount || 0,
            practiceNumber: s.practiceNumber,
            date: formatDate(dt),
            description: `${num}${parentInv ? ` for Invoice-${parentInv}` : ""}`,
            status: "",
            paymentSource: s.paymentSource,
            receiptFromPatient: fromPatient,
            receiptFromMedicalAid: fromMedAid,
            balance: s.runningBalance,
          });
        }
      });

      // Closing Balance
      transactions.push({
        date: formatDate(endDate),
        description: "Closing Balance",
        invoiceAmount: 0,
        receiptFromPatient: 0,
        receiptFromMedicalAid: 0,
        balance: closingBalance,
      });

      setTransactions(transactions);

      const agedBalances = await getAgedBalancesByAccount(accountId);
      setAgedBalances(agedBalances.agedBalances);
      setUnallocatedBalance(agedBalances.unallocatedTotal);

    } catch (error) {
      console.error(
        "❌ Error fetching statements:",
        error.response?.data || error.message
      );
    }
  }, [accountId, startDate, endDate, practiceIds]);

  useEffect(() => {
    if (accountId && startDate && endDate && practiceIds.length > 0) {
      fetchData();
    }
  }, [fetchData, accountId, startDate, endDate, practiceIds]);

  function buildReceiptEmailData({ receipt, account, practice }) {
    const practiceNumber = practice?.practiceNumber || "";
    const patientFirstName = account?.accountFullNames || "";
    const patientLastName = account?.accountLastName || "";

    return {
      practiceNumbers: practiceNumber ? [String(practiceNumber)] : [],
      practiceName: practice?.practiceName || "",
      practiceEmail: practice?.practiceEmail || "",
      practicePhone: practice?.practicePhoneNumber || "",
      practiceNumber,
      logoURL: practice?.logoURL || "",

      patientFirstName,
      patientLastName,
      patientName: [patientFirstName, patientLastName]
        .filter(Boolean)
        .join(" "),

      receiptNumber: receipt?.receiptNumber || "",
      receiptDate: receipt?.receiptDate || "",
    };
  }

  function buildCreditNoteEmailData({ creditNote, account, practice }) {
    const practiceNumber = practice?.practiceNumber || "";
    const patientFirstName = account?.accountFullNames || "";
    const patientLastName = account?.accountLastName || "";

    return {
      practiceNumbers: practiceNumber ? [String(practiceNumber)] : [],
      practiceName: practice?.practiceName || "",
      practiceEmail: practice?.practiceEmail || "",
      practicePhone: practice?.practicePhoneNumber || "",
      practiceNumber,
      logoURL: practice?.logoURL || "",

      patientFirstName,
      patientLastName,
      patientName: [patientFirstName, patientLastName]
        .filter(Boolean)
        .join(" "),

      creditNoteNumber: creditNote?.creditNoteNumber || "",
      creditNoteDate: creditNote?.creditNoteDate || "",
    };
  }

  async function exportPDF() {
    try {
      const practiceNumbers = [
        ...new Set(
          transactions
            .map((t) => t.practiceNumber)
            .filter(Boolean)
        ),
      ];

      const blob = await getStatementPDF(accountId, startDate, endDate, practiceNumbers.length > 0 ? practiceNumbers : practiceIds);

      const blobURL = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobURL;
      const accountName = account?.accountFullNames || "Account";
      link.download = `Statement_${accountName}_${startDate}_to_${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobURL);
    } catch (error) {
      console.error("Error exporting statement PDF:", error);
      alert("Failed to export PDF. Please try again.");
    }
  }

  async function prepareEmailStatement() {
    try {
      const practiceNumbers = [
        ...new Set(
          transactions
            .map((t) => t.practiceNumber)
            .filter(Boolean)
        ),
      ];

      const blob = await getStatementPDF(accountId, startDate, endDate, practiceNumbers.length > 0 ? practiceNumbers : practiceIds);

      // Still need statement data for the email modal template variables
      const data = await getStatementToExport(
        accountId,
        startDate,
        endDate,
        practiceIds
      );

      setStatementData(data);
      setStatementBlob(blob);
      setShowStatementEmailModal(true);
    } catch (error) {
      console.error("Failed to prepare email statement:", error);
      alert("Failed to prepare email statement. Please try again.");
    }
  }

  const handleCreateInvoice = (e) => {
    e?.stopPropagation();
    navigate(`/invoices/1/${account._id}`);
  };

  const handleCreateReceipt = (e) => {
    setReceiptAccount(account);
    setShowReceiptModal(true); // ✅ Show receipt modal
  };
  const handleCreditNote = (e) => {
    setCreditNoteAccount(account);
    setShowCreditNoteModal(true);
  };
  const handleWriteOff = () => {
    setShowWriteOffModal(true);
  };

  async function fetchReceiptData(receiptNumber) {
    const isCreditNote = receiptNumber?.includes("CN-");

    const [receiptDataImported, cnDataImported] = isCreditNote
      ? [null, await getCreditNoteByNumber(receiptNumber)]
      : [await getReceiptByNumber(receiptNumber), null];

    const practiceData = await getPracticeById(
      receiptDataImported?.receiptPracticeNumber ||
        cnDataImported?.creditNotePracticeNumber
    );

    const receiptData = {
      receiptNumber:
        receiptDataImported?.receiptNumber || cnDataImported?.creditNoteNumber,
      receiptDate:
        receiptDataImported?.receiptDate || cnDataImported?.creditNoteDate,
      receiptTotalAmount:
        receiptDataImported?.receiptTotalAmount ||
        cnDataImported?.creditNoteTotalAmount ||
        0,
      receiptInvoices:
        receiptDataImported?.receiptInvoices ||
        cnDataImported?.creditNoteInvoiceLines ||
        [],
      receiptPaymentMethod:
        receiptDataImported?.receiptMethod ||
        cnDataImported?.creditNotePaymentMethod ||
        "",
      receiptReference:
        receiptDataImported?.receiptInvoices?.[0]?.receiptReference ||
        cnDataImported?.creditNoteReference ||
        "",
      practice: practiceData,
      account,
    };

    return { receiptData, practiceData, isCreditNote };
  }

  async function handleDownloadClicked() {
    if (!selectedReceiptEntry) return;

    const { receiptData } = await fetchReceiptData(selectedReceiptEntry.receiptNumber);
    await ExportPDFReceipt(receiptData);
    setShowReceiptActionModal(false);
  }

  async function handleSendEmail() {
    if (!selectedReceiptEntry) return;

    try {
      const { receiptData, practiceData, isCreditNote } = await fetchReceiptData(
        selectedReceiptEntry.receiptNumber
      );

      const blob = await ExportPDFReceipt(receiptData, { asBlob: true });

      const emailVars = isCreditNote
        ? buildCreditNoteEmailData({
            creditNote: {
              creditNoteNumber: receiptData.receiptNumber,
              creditNoteDate: receiptData.receiptDate,
            },
            account,
            practice: practiceData,
          })
        : buildReceiptEmailData({
            receipt: {
              receiptNumber: receiptData.receiptNumber,
              receiptDate: receiptData.receiptDate,
            },
            account,
            practice: practiceData,
          });

      setDocEmailBlob(blob);
      setDocEmailType(isCreditNote ? "creditNote" : "receipt");
      setDocEmailData(emailVars);
      setShowReceiptEmailModal(true);
      setShowReceiptActionModal(false);
    } catch (error) {
      console.error("Failed to prepare receipt/credit note email:", error);
      alert("Failed to prepare document for email. Please try again.");
    }
  }

  async function handleSaveEdi() {
    try {
      await updateAccountEdi(account._id, ediDisabled, ediEndDate);
      setShowEdiModal(false);
    } catch (error) {
      console.error("Failed to save EDI settings:", error);
      alert("Failed to save EDI settings.");
    }
  }

  const hasMedicalAid = account?.accountMedicalAid && account.accountMedicalAid !== "";
  const ediActive = ediDisabled && ediEndDate && new Date(ediEndDate) >= new Date(new Date().toISOString().split("T")[0]);

  const hasWrittenOff = transactions.some((t) => t.status === "Written Off");
  const canNavigate = userRole;

  // Surface EDI-disabled status + written-off flag to the parent page so the
  // header row can render the relevant badges next to the section heading.
  useEffect(() => {
    onStateChange?.({ ediActive: !!ediActive, ediEndDate, hasWrittenOff });
  }, [ediActive, ediEndDate, hasWrittenOff, onStateChange]);

  // Shared statement-entry click handler used by both the desktop table row
  // and the mobile card. Opens the receipt-action modal for receipts/refunds,
  // navigates to the invoice page for invoice entries, builds the correct
  // breadcrumb trail for the destination.
  const handleEntryClick = (entry, isReceiptEntry, isWriteOffEntry) => {
    if (!canNavigate) return;
    if (isReceiptEntry) {
      setSelectedReceiptEntry({ receiptNumber: entry.receiptNumber });
      setShowReceiptActionModal(true);
      return;
    }
    if (!isWriteOffEntry && entry.invoice) {
      const specialStatuses = ["Draft", "Pending"];
      const path = specialStatuses.includes(entry.status)
        ? `/invoices/${entry.invoice._id}/${account._id}`
        : `/invoices/posted/${entry.invoice._id}`;
      const fi = (account?.accountFullNames || "").charAt(0);
      const accountName =
        `Account - ${fi ? fi + " " : ""}${account?.accountLastName || ""}`.trim() || "Account";

      const incomingTrail = location.state?.breadcrumbTrail;
      let fullTrail;
      if (incomingTrail?.length) {
        fullTrail = [...incomingTrail, { label: accountName, path: `/accounts/${account._id}` }];
      } else if (breadcrumbContext?.fromAppointment) {
        fullTrail = [
          { label: "Appointments", path: getBookingsReturnPath() },
          { label: breadcrumbContext.appointmentLabel, path: breadcrumbContext.appointmentPath },
          { label: accountName, path: `/accounts/${account._id}` },
        ];
      } else if (breadcrumbContext?.fromDashboard) {
        fullTrail = [
          { label: "Dashboard", path: "/home" },
          { label: breadcrumbContext.patientName, path: `/patients/appointment/${breadcrumbContext.patientId}/1` },
          { label: accountName, path: `/accounts/${account._id}` },
        ];
      } else if (breadcrumbContext?.fromPatient) {
        fullTrail = [
          { label: breadcrumbContext.patientName, path: `/patients/appointment/${breadcrumbContext.patientId}/1` },
          { label: accountName, path: `/accounts/${account._id}` },
        ];
      } else {
        fullTrail = [
          { label: "Accounts", path: "/billing" },
          { label: accountName, path: `/accounts/${account._id}` },
        ];
      }

      navigate(path, {
        state: { account, fromStatement: true, accountName, breadcrumbTrail: fullTrail },
      });
    }
  };

  // Expose action triggers + relevant state to the parent (Accounts.jsx)
  // so the page-level icon rail can drive EDI / Create Invoice / Receipt /
  // Credit Note / Write Off / Download / Email / Bulk Export from outside
  // this component. State is included so the rail can gate or label items.
  useImperativeHandle(
    ref,
    () => ({
      // booleans the rail needs to know about
      hasMedicalAid: !!hasMedicalAid,
      ediActive: !!ediActive,
      ediEndDate,
      userRole,
      canExport: !!(accountId && startDate && endDate),
      // action triggers
      openEdi: () => setShowEdiModal(true),
      createInvoice: () => handleCreateInvoice(),
      createReceipt: () => handleCreateReceipt(),
      createCreditNote: () => handleCreditNote(),
      createWriteOff: () => handleWriteOff(),
      downloadPdf: () => exportPDF(),
      emailStatement: () => prepareEmailStatement(),
      bulkExport: () => setShowBulkExport(true),
    }),
    [hasMedicalAid, ediActive, ediEndDate, userRole, accountId, startDate, endDate],
  );

  return (
    <div className="acc-list">
      <Container fluid className="mb-2 px-4">
        {" "}
        {/* <-- Consistent horizontal padding */}
        <Row>
          <Col xs={12} xl={2}>
            {/* Heading + date filters share the slim left panel at xl+, and
                stack as a full-width row above the table at <xl. EDI + Actions
                live in the page-level rail (Accounts.jsx); the Written Off /
                EDI-disabled badges live in the page-header row above the
                icon-rail panels. */}
            <h5 className="text-primary mb-2">Account Statement</h5>
            <Form className="d-flex flex-xl-column gap-2 mb-2">
              <Form.Group className="flex-fill">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="flex-fill">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Form>
          </Col>

          <Col xs={12} xl={10}>
            {/* Desktop: full statement table */}
            <div className="d-none d-lg-block">
              <Table bordered hover className="mt-1">
                <thead className="text-center">
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Invoice | Receipt</th>
                    <th>Patient</th>
                    <th>Medical Aid</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((entry, index) => {
                    const isPending = entry.status === "Pending";
                    const isReceiptEntry =
                      entry.description?.includes("Receipt") ||
                      ((entry.description?.includes("RCT-") ||
                        entry.description?.includes("CN-") ||
                        entry.description?.includes("WO-")) &&
                        !entry.invoiceId &&
                        entry.description !== "Opening Balance" &&
                        entry.description !== "Closing Balance");
                    const isRefundEntry = entry.description?.includes("RF-");
                    const isWriteOffEntry = entry.description?.includes("WO-");
                    const isSubEntry = isReceiptEntry || isRefundEntry || isWriteOffEntry;
                    const canTap =
                      canNavigate &&
                      (isReceiptEntry || (!isWriteOffEntry && entry.invoice));
                    // Group-end: row is the last visible entry, or the next
                    // entry is a new main (not a sub-entry). Draws a thicker
                    // separator beneath so invoice groupings read clearly.
                    const next = transactions[index + 1];
                    const nextIsSub = next
                      ? next.description?.includes("Receipt") ||
                        ((next.description?.includes("RCT-") ||
                          next.description?.includes("CN-") ||
                          next.description?.includes("WO-")) &&
                          !next.invoiceId &&
                          next.description !== "Opening Balance" &&
                          next.description !== "Closing Balance") ||
                        next.description?.includes("RF-") ||
                        next.description?.includes("WO-")
                      : false;
                    const isGroupEnd = !next || !nextIsSub;
                    return (
                      <tr
                        key={`entry-${index}`}
                        className={isGroupEnd ? "acc-statement-group-end" : ""}
                        onClick={() => handleEntryClick(entry, isReceiptEntry, isWriteOffEntry)}
                        style={{ cursor: canTap ? "pointer" : "default" }}
                        title={
                          canNavigate
                            ? isReceiptEntry || entry.invoice
                              ? "Open details"
                              : ""
                            : "Insufficient permissions"
                        }
                      >
                        <td>{entry.date || ""}</td>
                        <td>
                          {isSubEntry ? (
                            <span className="ms-3">{entry.description}</span>
                          ) : isPending ? (
                            <span>{entry.description}</span>
                          ) : (
                            <span className="fw-semibold">{entry.description}</span>
                          )}
                        </td>
                        <td>
                          {isPending ? (
                            <em>{entry.status}</em>
                          ) : (
                            <span className="fw-semibold">{entry.status}</span>
                          )}
                        </td>
                        <td className="acc-list-num">
                          {isRefundEntry
                            ? `R ${parseFloat(entry.paymentAmount || 0).toFixed(2)}`
                            : entry.invoiceAmount
                              ? `R ${entry.invoiceAmount.toFixed(2)}`
                              : ""}
                        </td>
                        <td className="acc-list-num">
                          {(isReceiptEntry || isWriteOffEntry) && entry.receiptFromPatient
                            ? `(R ${parseFloat(entry.receiptFromPatient).toFixed(2)})`
                            : ""}
                        </td>
                        <td className="acc-list-num">
                          {(isReceiptEntry || isWriteOffEntry) && entry.receiptFromMedicalAid
                            ? `(R ${parseFloat(entry.receiptFromMedicalAid).toFixed(2)})`
                            : ""}
                        </td>
                        <td className={`acc-list-num${isPending ? "" : " fw-bold"}`}>
                          {entry.balance != null ? `R ${entry.balance.toFixed(2)}` : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Mobile: card per invoice-group. A group = one "main" entry
                (invoice / opening / closing balance) plus the receipts /
                refunds / write-offs that follow it before the next main.
                Standalone sub-entries (no preceding main in the filtered
                view) become their own card. */}
            <div className="d-lg-none mt-1">
              {(() => {
                const flagsOf = (e) => {
                  if (!e) return null;
                  const isReceiptEntry =
                    e.description?.includes("Receipt") ||
                    ((e.description?.includes("RCT-") ||
                      e.description?.includes("CN-") ||
                      e.description?.includes("WO-")) &&
                      !e.invoiceId &&
                      e.description !== "Opening Balance" &&
                      e.description !== "Closing Balance");
                  const isRefundEntry = e.description?.includes("RF-");
                  const isWriteOffEntry = e.description?.includes("WO-");
                  return {
                    isReceiptEntry,
                    isRefundEntry,
                    isWriteOffEntry,
                    isSubEntry: isReceiptEntry || isRefundEntry || isWriteOffEntry,
                  };
                };

                // Build groups
                const groups = [];
                let current = null;
                transactions.forEach((entry) => {
                  const f = flagsOf(entry);
                  if (f.isSubEntry && current) {
                    current.subs.push({ entry, flags: f });
                  } else if (f.isSubEntry && !current) {
                    groups.push({
                      main: null,
                      mainFlags: null,
                      subs: [{ entry, flags: f }],
                    });
                  } else {
                    current = { main: entry, mainFlags: f, subs: [] };
                    groups.push(current);
                  }
                });

                const extractRctLabel = (desc) =>
                  desc?.match(/(RCT-\S+|CN-\S+|WO-\S+|RF-\S+)/)?.[0] || "";

                return groups.map((group, gi) => {
                  // Choose the "header" entry for the card. Prefer main, fall
                  // back to first sub for standalone-receipt cards.
                  const header = group.main || group.subs[0]?.entry;
                  const headerFlags = group.mainFlags || group.subs[0]?.flags;
                  const isPending = header?.status === "Pending";

                  // Card "Amount" row — sits in the body above Balance, same
                  // font as the sub-entry rows. Invoice cards show the invoice
                  // amount; standalone receipts/refunds/write-offs show their
                  // own amount; Opening/Closing Balance show nothing here
                  // (their value is the Balance row itself).
                  let amountText = null;
                  let amountIsNeg = false;
                  if (group.main && headerFlags && !headerFlags.isSubEntry) {
                    if (
                      header.invoiceAmount &&
                      header.description !== "Opening Balance" &&
                      header.description !== "Closing Balance"
                    ) {
                      amountText = `R ${header.invoiceAmount.toFixed(2)}`;
                    }
                  } else if (headerFlags?.isRefundEntry) {
                    amountText = `R ${parseFloat(header.paymentAmount || 0).toFixed(2)}`;
                  } else if (headerFlags?.isReceiptEntry || headerFlags?.isWriteOffEntry) {
                    const total =
                      parseFloat(header.receiptFromPatient || 0) +
                      parseFloat(header.receiptFromMedicalAid || 0);
                    if (total > 0) {
                      amountText = `(R ${total.toFixed(2)})`;
                      amountIsNeg = true;
                    }
                  }

                  const canTap =
                    canNavigate &&
                    !!header &&
                    (headerFlags?.isReceiptEntry ||
                      (!headerFlags?.isWriteOffEntry && header.invoice));
                  const onActivate = () =>
                    handleEntryClick(
                      header,
                      headerFlags?.isReceiptEntry,
                      headerFlags?.isWriteOffEntry,
                    );

                  // Running balance for the group. Per the data construction
                  // above, under-invoice receipts deliberately have NO balance
                  // field — the invoice row's runningBalance already encodes
                  // the post-receipts final balance for the whole group. For
                  // standalone-sub groups (no main), the sub itself carries it.
                  const lastSub = group.subs[group.subs.length - 1];
                  const balanceEntry = group.main
                    ? header
                    : lastSub?.entry ?? header;
                  const showBalance = balanceEntry?.balance != null;

                  return (
                    <div
                      className="acc-card"
                      key={
                        header?.invoice?._id ||
                        header?.receiptNumber ||
                        `g-${gi}`
                      }
                      onClick={canTap ? onActivate : undefined}
                      role={canTap ? "button" : undefined}
                      tabIndex={canTap ? 0 : undefined}
                      onKeyDown={
                        canTap
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onActivate();
                              }
                            }
                          : undefined
                      }
                      style={{ cursor: canTap ? "pointer" : "default" }}
                    >
                      <div className="acc-card-top">
                        <span
                          className="acc-card-id"
                          title={header?.description}
                        >
                          {header?.description}
                        </span>
                      </div>
                      <div className="acc-card-body">
                        {header?.date && (
                          <div className="acc-card-field">
                            <span className="acc-card-label">Date</span>
                            <span className="acc-card-value">{header.date}</span>
                          </div>
                        )}
                        {header?.status && (
                          <div className="acc-card-field">
                            <span className="acc-card-label">Status</span>
                            <span className="acc-card-value">
                              {isPending ? <em>{header.status}</em> : header.status}
                            </span>
                          </div>
                        )}
                        {amountText && (
                          <div className="acc-card-field">
                            <span className="acc-card-label">Amount</span>
                            <span
                              className={`acc-card-value acc-list-num${
                                amountIsNeg ? " acc-card-amount-neg" : ""
                              }`}
                            >
                              {amountText}
                            </span>
                          </div>
                        )}

                        {/* Per-sub rows: e.g. "Patient (RCT-12345) — (R 250)" */}
                        {group.subs.map((sub, si) => {
                          const fromPat = parseFloat(sub.entry.receiptFromPatient || 0);
                          const fromMA = parseFloat(sub.entry.receiptFromMedicalAid || 0);
                          const refundAmt = sub.flags.isRefundEntry
                            ? parseFloat(sub.entry.paymentAmount || 0)
                            : 0;
                          const rctLabel = extractRctLabel(sub.entry.description);
                          return (
                            <React.Fragment key={`g-${gi}-s-${si}`}>
                              {fromPat > 0 && (
                                <div className="acc-card-field">
                                  <span className="acc-card-label">
                                    Patient{rctLabel ? ` (${rctLabel})` : ""}
                                  </span>
                                  <span className="acc-card-value acc-list-num acc-card-amount-neg">
                                    (R {fromPat.toFixed(2)})
                                  </span>
                                </div>
                              )}
                              {fromMA > 0 && (
                                <div className="acc-card-field">
                                  <span className="acc-card-label">
                                    Med Aid{rctLabel ? ` (${rctLabel})` : ""}
                                  </span>
                                  <span className="acc-card-value acc-list-num acc-card-amount-neg">
                                    (R {fromMA.toFixed(2)})
                                  </span>
                                </div>
                              )}
                              {refundAmt > 0 && (
                                <div className="acc-card-field">
                                  <span className="acc-card-label">
                                    Refund{rctLabel ? ` (${rctLabel})` : ""}
                                  </span>
                                  <span className="acc-card-value acc-list-num">
                                    R {refundAmt.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}

                        {showBalance && (
                          <div className="acc-card-field">
                            <span className="acc-card-label">Balance</span>
                            <span
                              className={`acc-card-value acc-list-num${
                                isPending ? "" : " acc-card-amount-running"
                              }`}
                            >
                              R {balanceEntry.balance.toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Unallocated Receipts — only shown when there are unallocated funds */}
            {unallocatedReceipts.length > 0 && (
              <>
                <div className="d-flex align-items-center gap-2 mt-3 mb-1">
                  <Form.Check
                    type="checkbox"
                    id="showOnStatement"
                    label="Show unallocated funds on statement"
                    checked={showOnStatement}
                    onChange={async (e) => {
                      const newVal = e.target.checked;
                      setShowOnStatement(newVal);
                      try {
                        await updateAccountShowOnStatement(account._id, newVal);
                      } catch (err) {
                        console.error("Failed to save show on statement preference:", err);
                        setShowOnStatement(!newVal); // revert on failure
                      }
                    }}
                  />
                </div>
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <h6 className="mb-0 text-primary fw-bold d-none d-lg-block">
                    Unallocated Receipts
                  </h6>
                  <h6 className="mb-0 text-primary fw-bold d-lg-none">
                    Unallocated Funds
                  </h6>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowAllUnallocated((v) => !v)}
                  >
                    {showAllUnallocated ? "Hide fully used" : "Show all"}
                  </Button>
                </div>
                {(() => {
                  const visible = unallocatedReceipts.filter((r) => {
                    const balance = (r.paymentAmount || 0) - (r.usedAmount || 0);
                    return showAllUnallocated || balance > 0;
                  });
                  return (
                    <>
                      {/* Desktop: compact summary table */}
                      <div className="d-none d-lg-block">
                        <Table responsive size="sm">
                          <thead className="text-center">
                            <tr>
                              <th>Receipt #</th>
                              <th>Date</th>
                              <th className="text-end">Received</th>
                              <th className="text-end">Medical Aid</th>
                              <th className="text-end">Patient</th>
                              <th className="text-end">Used</th>
                              <th className="text-end">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visible.map((r, idx) => {
                              const received = r.paymentAmount || 0;
                              const used = r.usedAmount || 0;
                              const isPatient = ["Patient", "patient"].includes(r.paymentSource);
                              const isMedAid = ["Medical Aid", "medicalAid"].includes(r.paymentSource);
                              const balance = r.runningBalance ?? received - used;
                              return (
                                <tr key={`unalloc-${idx}`}>
                                  <td>{r.receiptNumber || ""}</td>
                                  <td>{formatDate(r.receiptDate)}</td>
                                  <td className="acc-list-num">R {received.toFixed(2)}</td>
                                  <td className="acc-list-num">
                                    {isMedAid ? `R ${received.toFixed(2)}` : ""}
                                  </td>
                                  <td className="acc-list-num">
                                    {isPatient ? `R ${received.toFixed(2)}` : ""}
                                  </td>
                                  <td className="acc-list-num">R {used.toFixed(2)}</td>
                                  <td className="acc-list-num fw-bold">R {balance.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      {/* Mobile: card per receipt */}
                      <div className="d-lg-none">
                        {visible.map((r, idx) => {
                          const received = r.paymentAmount || 0;
                          const used = r.usedAmount || 0;
                          const isPatient = ["Patient", "patient"].includes(r.paymentSource);
                          const isMedAid = ["Medical Aid", "medicalAid"].includes(r.paymentSource);
                          const balance = r.runningBalance ?? received - used;
                          const source = isPatient
                            ? "Patient"
                            : isMedAid
                              ? "Medical Aid"
                              : r.paymentSource || "—";
                          return (
                            <div className="acc-card" key={`unalloc-card-${idx}`}>
                              <div className="acc-card-top">
                                <span className="acc-card-id">
                                  {r.receiptNumber || "(no #)"}
                                </span>
                                <span className="acc-card-amount">R {received.toFixed(2)}</span>
                              </div>
                              <div className="acc-card-body">
                                <div className="acc-card-field">
                                  <span className="acc-card-label">Date</span>
                                  <span className="acc-card-value">{formatDate(r.receiptDate)}</span>
                                </div>
                                <div className="acc-card-field">
                                  <span className="acc-card-label">Source</span>
                                  <span className="acc-card-value">{source}</span>
                                </div>
                                <div className="acc-card-field">
                                  <span className="acc-card-label">Used</span>
                                  <span className="acc-card-value acc-list-num">
                                    R {used.toFixed(2)}
                                  </span>
                                </div>
                                <div className="acc-card-field">
                                  <span className="acc-card-label">Balance</span>
                                  <span className="acc-card-value acc-list-num fw-bold">
                                    R {balance.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            {(() => {
              const patient = {
                d30: agedBalances.days30?.patient || 0,
                d60: agedBalances.days60?.patient || 0,
                d90: agedBalances.days90?.patient || 0,
                d120: agedBalances.days120plus?.patient || 0,
              };
              const medAid = {
                d30: agedBalances.days30?.medicalAid || 0,
                d60: agedBalances.days60?.medicalAid || 0,
                d90: agedBalances.days90?.medicalAid || 0,
                d120: agedBalances.days120plus?.medicalAid || 0,
              };
              const patientTotal = patient.d30 + patient.d60 + patient.d90 + patient.d120;
              const medAidTotal = medAid.d30 + medAid.d60 + medAid.d90 + medAid.d120;
              return (
                <>
                  {/* Desktop: aged balances summary table — right-aligned 60% wide */}
                  <div className="d-none d-lg-flex justify-content-end mt-4">
                    <div style={{ width: "60%" }}>
                      <Table bordered size="sm">
                        <thead className="text-center">
                          <tr>
                            <th></th>
                            <th>Outstanding</th>
                            <th>30 Days</th>
                            <th>60 Days</th>
                            <th>90 Days</th>
                            <th>120+ Days</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="fw-bold">Patient</td>
                            <td className="acc-list-num text-danger fw-bold">
                              R {patientTotal.toFixed(2)}
                            </td>
                            <td className="acc-list-num">R {patient.d30.toFixed(2)}</td>
                            <td className="acc-list-num">R {patient.d60.toFixed(2)}</td>
                            <td className="acc-list-num">R {patient.d90.toFixed(2)}</td>
                            <td className="acc-list-num">R {patient.d120.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Medical Aid</td>
                            <td className="acc-list-num text-danger fw-bold">
                              R {medAidTotal.toFixed(2)}
                            </td>
                            <td className="acc-list-num">R {medAid.d30.toFixed(2)}</td>
                            <td className="acc-list-num">R {medAid.d60.toFixed(2)}</td>
                            <td className="acc-list-num">R {medAid.d90.toFixed(2)}</td>
                            <td className="acc-list-num">R {medAid.d120.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Unallocated Funds</td>
                            <td className="acc-list-num fw-bold text-success">
                              R {unallocatedBalance.toFixed(2)}
                            </td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  {/* Mobile: single card with 3 columns — Patient / Medical
                      Aid / Unallocated. Header row per col shows the role and
                      its outstanding total; the bucket rows align horizontally
                      across cols so the eye can compare 30/60/90/120+ at a
                      glance. Unallocated has no bucket breakdown so its col
                      only renders the header. */}
                  <div className="d-lg-none mt-4">
                    <h6 className="mb-2 text-primary fw-bold">Aged Analysis</h6>
                    <div className="acc-card">
                      <div className="aged-analysis-grid">
                        <div className="aged-analysis-col">
                          <div className="aged-analysis-header">
                            <span className="aged-analysis-role">Patient</span>
                            <span className="aged-analysis-total text-danger">
                              R {patientTotal.toFixed(2)}
                            </span>
                          </div>
                          <div className="aged-analysis-buckets">
                            <div className="aged-bucket">
                              <span className="bucket-label">30</span>
                              <span className="bucket-val">R {patient.d30.toFixed(2)}</span>
                            </div>
                            <div className="aged-bucket">
                              <span className="bucket-label">60</span>
                              <span className="bucket-val">R {patient.d60.toFixed(2)}</span>
                            </div>
                            <div className="aged-bucket">
                              <span className="bucket-label">90</span>
                              <span className="bucket-val">R {patient.d90.toFixed(2)}</span>
                            </div>
                            <div className="aged-bucket">
                              <span className="bucket-label">120+</span>
                              <span className="bucket-val">R {patient.d120.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="aged-analysis-col">
                          <div className="aged-analysis-header">
                            <span className="aged-analysis-role">Med Aid</span>
                            <span className="aged-analysis-total text-danger">
                              R {medAidTotal.toFixed(2)}
                            </span>
                          </div>
                          <div className="aged-analysis-buckets">
                            <div className="aged-bucket">
                              <span className="bucket-label">30</span>
                              <span className="bucket-val">R {medAid.d30.toFixed(2)}</span>
                            </div>
                            <div className="aged-bucket">
                              <span className="bucket-label">60</span>
                              <span className="bucket-val">R {medAid.d60.toFixed(2)}</span>
                            </div>
                            <div className="aged-bucket">
                              <span className="bucket-label">90</span>
                              <span className="bucket-val">R {medAid.d90.toFixed(2)}</span>
                            </div>
                            <div className="aged-bucket">
                              <span className="bucket-label">120+</span>
                              <span className="bucket-val">R {medAid.d120.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="aged-analysis-col">
                          <div className="aged-analysis-header">
                            <span className="aged-analysis-role">Unalloc</span>
                            <span className="aged-analysis-total text-success">
                              R {unallocatedBalance.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}

            <Modal
              show={showReceiptActionModal}
              onHide={() => setShowReceiptActionModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Receipt Options</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Would you like to download or email this receipt?</p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={handleDownloadClicked}>
                  Download
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleSendEmail}
                >
                  Email
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowReceiptActionModal(false)}
                >
                  Cancel
                </Button>
              </Modal.Footer>
            </Modal>
                <EmailDocumentModal
                  show={showReceiptEmailModal}
                  onHide={() => setShowReceiptEmailModal(false)}
                  title={
                    docEmailType === "creditNote"
                      ? "Email Credit Note"
                      : "Email Receipt"
                  }
                  defaultEmail={account?.accountEmail}
                  email2={account?.accountEmail2}
                  email3={account?.accountEmail3}
                  email4={account?.accountEmail4}
                  email5={account?.accountEmail5}
                  helperText="The document will be sent as a PDF attachment to all email addresses entered above."
                  onSend={(emails) => {
                    if (!docEmailBlob) return;

                    // snapshot current state
                    const blob = docEmailBlob;
                    const edata = docEmailData;
                    const dtype = docEmailType; // "receipt" | "creditNote"
                    const acctId = account?._id;

                    // close + reset immediately
                    setShowReceiptEmailModal(false);
                    setDocEmailBlob(null);
                    setDocEmailData(null);

                    // fire-and-forget
                    return sendInvoiceByEmail({
                      emails, // ✅ multi email list
                      pdfBlob: blob,
                      type: dtype,
                      accountId: acctId,
                      emailData: edata,
                    });
                  }}
                />
          </Col>
        </Row>
      </Container>
      <BulkExportModal
        show={showBulkExport}
        onHide={() => setShowBulkExport(false)}
        accountId={accountId}
      />
      {showCreditNoteModal && creditNoteAccount && (
        <CreditNote
          show={showCreditNoteModal}
          onHide={() => setShowCreditNoteModal(false)}
          account={creditNoteAccount}
        />
      )}

      {showWriteOffModal && (
        <WriteOffModal
          show={showWriteOffModal}
          onHide={() => setShowWriteOffModal(false)}
          account={account}
        />
      )}

      {showReceiptModal && receiptAccount && (
        <CreateReceiptModal
          show={showReceiptModal}
          onHide={() => setShowReceiptModal(false)}
          account={receiptAccount}
        />
      )}

      <Modal show={showEdiModal} onHide={() => setShowEdiModal(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Set EDI</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="edi-disabled-switch"
              label="Disable EDI"
              checked={ediDisabled}
              onChange={(e) => setEdiDisabled(e.target.checked)}
            />
          </Form.Group>
          {ediDisabled && (
            <Form.Group className="mb-3">
              <Form.Label>Disable until</Form.Label>
              <Form.Control
                type="date"
                value={ediEndDate}
                onChange={(e) => setEdiEndDate(e.target.value)}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEdiModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdi} disabled={ediDisabled && !ediEndDate}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {showStatementEmailModal && statementBlob && (
        <EmailStatementModal
          show={showStatementEmailModal}
          onHide={() => setShowStatementEmailModal(false)}
          defaultEmail={account.accountEmail}
          email2={account.accountEmail2}
          email3={account.accountEmail3}
          email4={account.accountEmail4}
          email5={account.accountEmail5}
          statementData={statementData}
          pdfBlob={statementBlob}
          sendStatementByEmail={sendStatementByEmail}
        />
      )}
    </div>
  );
});

export default AccountStatements;
